// Small shared presentational primitives for the console: verdict chip,
// disposition badge, progress bar, spinner, error box, toast. No data logic.
//
// The verdict chip is the same object the customer surfaces render — mark
// (shape + glyph) plus the word, colour last — so counsel switching between
// the practice's view and this console sees one verdict language. The
// DISPOSITION badge is deliberately achromatic with its own glyphs: what the
// attorney decided is an action, not a verdict, and must never borrow the
// verdict palette.

import { useEffect } from 'react';
import { Check, Circle, CircleAlert, Clock, X } from 'lucide-react';
import type { Disposition, GroupStatus, Verdict } from '../api/types';

const VERDICT_LABEL: Record<Verdict, string> = {
  compliant: 'Compliant',
  grey: 'Needs review',
  non_compliant: 'Non-compliant',
};

const VERDICT_CLS: Record<Verdict, 'ok' | 'und' | 'no'> = {
  compliant: 'ok',
  grey: 'und',
  non_compliant: 'no',
};

export function verdictLabel(v: Verdict): string {
  return VERDICT_LABEL[v];
}

/** The bare mark: circle-check / diamond-dot / square-bar. */
export function VerdictMark({ verdict, large = false }: { verdict: Verdict; large?: boolean }) {
  return (
    <span
      className={`vx-mark vx-mark--${VERDICT_CLS[verdict]}${large ? ' vx-mark--lg' : ''}`}
      aria-hidden="true"
    />
  );
}

export function VerdictChip({ verdict }: { verdict: Verdict }) {
  return (
    <span className={`vx-chip vx-chip--${VERDICT_CLS[verdict]}`}>
      <VerdictMark verdict={verdict} />
      {VERDICT_LABEL[verdict]}
    </span>
  );
}

export function DispositionBadge({ disposition }: { disposition: Disposition | null }) {
  // "Not reviewed", not "Open". The other three labels are past participles naming a
  // state — Accepted / Rejected / Deferred — and "Open" was the only one that also
  // reads as an imperative, sitting a few pixels from the row's real button. A
  // reviewer told us the "Open button doesn't open anything"; they were clicking this
  // span. Matching the grammar of the set removes the invitation.
  //
  // Deliberately NOT changed: the "N open" COUNTS and the group status pill. There
  // "open" is a quantity or sits in an explicit Open/Partial/Done trio, so context
  // already fixes the reading — and renaming every instance would cost the shared
  // vocabulary those counts share with the API's `open` field.
  if (!disposition)
    return (
      <span className="cx-disp cx-disp--open">
        <Circle size={9} aria-hidden="true" />
        Not reviewed
      </span>
    );
  if (disposition.type === 'accept')
    return (
      <span className="cx-disp cx-disp--accept">
        <Check size={11} strokeWidth={3} aria-hidden="true" />
        Accepted
      </span>
    );
  if (disposition.type === 'override')
    return (
      <span className="cx-disp cx-disp--override" title={disposition.override_reason ?? undefined}>
        <X size={11} strokeWidth={3} aria-hidden="true" />
        Rejected
        {disposition.final_verdict ? ` · now ${VERDICT_LABEL[disposition.final_verdict]}` : ''}
      </span>
    );
  // A confirmation's own verbs (migration 0130): "Rule applies" reads like an
  // accept, "Rule does not apply" reads like an override — same glyphs, the
  // confirmation's own words instead of a verdict-shaped one.
  if (disposition.type === 'confirm_applies')
    return (
      <span className="cx-disp cx-disp--accept">
        <Check size={11} strokeWidth={3} aria-hidden="true" />
        Rule applies
      </span>
    );
  if (disposition.type === 'confirm_not_applicable')
    return (
      <span className="cx-disp cx-disp--override" title={disposition.override_reason ?? undefined}>
        <X size={11} strokeWidth={3} aria-hidden="true" />
        Rule does not apply
      </span>
    );
  return (
    <span className="cx-disp cx-disp--defer" title={disposition.override_reason ?? undefined}>
      <Clock size={11} aria-hidden="true" />
      Deferred
    </span>
  );
}

export function GroupStatusPill({ status }: { status: GroupStatus }) {
  const label = status === 'done' ? 'Done' : status === 'partial' ? 'Partial' : 'Open';
  return (
    <span className={`cx-status cx-status--${status}`}>
      {status === 'done' && <Check size={11} strokeWidth={3} aria-hidden="true" />}
      {label}
    </span>
  );
}

export function StateChip({ code }: { code: string }) {
  return <span className="vx-tag vx-tag--mono">{code}</span>;
}

// `label` is what a screen reader calls the bar ("Review progress"); a
// progressbar with no accessible name is a serious axe violation and read as
// an unnamed number (2026-09-04 accessibility scan).
export function ProgressBar({
  pct,
  slim,
  label = 'Review progress',
}: {
  pct: number;
  slim?: boolean;
  label?: string;
}) {
  const clamped = Math.max(0, Math.min(100, Math.round(pct)));
  return (
    <div
      className={`cx-progress${slim ? ' cx-progress--slim' : ''}`}
      role="progressbar"
      aria-label={label}
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className="cx-progress__fill" style={{ width: `${clamped}%` }} />
    </div>
  );
}

export function Spinner() {
  return <span className="vx-spinner" role="status" aria-label="Loading" />;
}

export function CenterMessage({ children }: { children: React.ReactNode }) {
  return <div className="center-msg">{children}</div>;
}

export function ErrorBox({ detail }: { detail: string }) {
  return (
    <div className="vx-callout vx-callout--strong" role="alert">
      <CircleAlert size={16} aria-hidden="true" />
      <div>{detail}</div>
    </div>
  );
}

export function Toast({
  message,
  kind,
  onDone,
}: {
  message: string;
  kind?: 'ok' | 'err';
  onDone: () => void;
}) {
  useEffect(() => {
    const t = setTimeout(onDone, kind === 'err' ? 5200 : 2600);
    return () => clearTimeout(t);
  }, [onDone, kind]);
  return (
    <div className={`vx-toast vx-rise${kind === 'err' ? ' vx-toast--err' : ''}`} onClick={onDone}>
      {kind === 'err' ? (
        <CircleAlert size={16} aria-hidden="true" />
      ) : (
        <Check size={16} aria-hidden="true" />
      )}
      <span>{message}</span>
    </div>
  );
}
